import type { SortingState } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import type { CompetitiveDataPointExtended } from '../model/types'

import React, { useMemo, useState } from 'react'
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import { isNil } from 'lodash/fp'
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/shared/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { formatInTimeZone } from '@/shared/tools/dates'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

export const SkuSidebarPricesTable = ({
	rows,
	onToggleSerie,
	visibleOnChart,
}: {
	rows: CompetitiveDataPointExtended[]
	onToggleSerie: (label: string) => void
	visibleOnChart: string[]
}) => {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SortingState>([])
	const columns = useMemo(
		() => getColumns({ onToggleSerie, visibleOnChart }, t),
		[visibleOnChart, onToggleSerie]
	)
	const table = useReactTable<CompetitiveDataPointExtended>({
		data: rows,
		columns,
		state: {
			sorting,
		},
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})

	return (
		<Table className='w-full table-fixed text-sm'>
			<colgroup>
				<col width='140px' />
				<col width='62px' />
				<col width='85px' />
				<col width='52px' />
				<col width='52px' />
			</colgroup>
			<TableHeader className='text-xs'>
				{table.getHeaderGroups().map((headerGroup) => (
					<TableRow key={headerGroup.id}>
						{headerGroup.headers.map((header) => (
							<TableHead
								key={header.id}
								className='overflow-hidden border-b p-2 text-left font-medium'
							>
								<div
									{...{
										className: header.column.getCanSort()
											? 'cursor-pointer select-none flex items-center'
											: '',
										onClick: header.column.getToggleSortingHandler(),
									}}
								>
									<span className='truncate'>
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
									</span>
									{{
										asc: <ArrowUpIcon className='size-3' strokeWidth={3} />,
										desc: <ArrowDownIcon className='size-3' strokeWidth={3} />,
									}[header.column.getIsSorted() as string] ?? null}
								</div>
							</TableHead>
						))}
					</TableRow>
				))}
			</TableHeader>
			<TableBody>
				{table.getRowModel().rows.map((row) => (
					<TableRow key={row.id} className='h-9'>
						{row.getVisibleCells().map((cell) => (
							<TableCell key={cell.id} className='truncate px-2'>
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
const columnHelper = createColumnHelper<CompetitiveDataPointExtended>()
const getColumns = (
	{
		onToggleSerie,
		visibleOnChart,
	}: {
		onToggleSerie: (label: string) => void
		visibleOnChart: string[]
	},
	t: TFunction
) => [
	columnHelper.accessor('competitor_name', {
		header: () => 'Name',
		cell: (info) => {
			const checked = visibleOnChart?.includes(info.row.original.label)
			const color = checked ? info.row.original.color! : '#b1bbca'

			return (
				<>
					<Tooltip>
						<TooltipTrigger asChild>
							<Point
								color={color}
								onChange={() => onToggleSerie(info.row.original.label)}
								checked={checked}
							/>
						</TooltipTrigger>
						<TooltipContent>
							{t('eastside_market_click_for_toggle')}
						</TooltipContent>
					</Tooltip>
					<MarketLabel item={info.row.original} />
				</>
			)
		},
	}),
	columnHelper.accessor('price', {
		header: () => t('app.price', { defaultValue: 'Price' }),
		cell: (info) => {
			const value = info.getValue()

			return value && !isNil(value) ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<span>{formatNumber(value, NumberFormats.Fixed)}</span>
					</TooltipTrigger>
					<TooltipContent>
						{formatNumber(value, NumberFormats.Fixed)}
					</TooltipContent>
				</Tooltip>
			) : (
				'-'
			)
		},
	}),
	columnHelper.accessor('observed_on', {
		header: () => t('general_period', { defaultValue: 'Period' }),
		cell: (info) => {
			const date = info.getValue()
			if (!date) {
				return 'No data'
			}

			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<span>
							{formatDistanceToNow(new Date(date), { addSuffix: true })}
						</span>
					</TooltipTrigger>
					<TooltipContent>
						{formatInTimeZone(new Date(date), 'MMMM d yyyy p')}
					</TooltipContent>
				</Tooltip>
			)
		},
	}),
	columnHelper.accessor('availability', {
		header: () => t('stock', { defaultValue: 'Stock' }),
		cell: (info) => {
			if (info.row.original.label === 'My price') {
				return '-'
			}

			return info.getValue() === 'InStock' ? (
				<CheckIcon className='size-4 text-success' />
			) : (
				<XIcon className='size-4 text-destructive' />
			)
		},
	}),
	columnHelper.accessor('promo_type_id', {
		header: () => t('promo', { defaultValue: 'Promo' }),
		cell: (info) => {
			if (info.row.original.label === 'My price') {
				return '-'
			}
			const value = info.getValue()
			// TODO: remove this hack in future PROD-4937

			return value === 'true' ? (
				<CheckIcon className='size-4 text-success' />
			) : (
				<XIcon className='size-4 text-destructive' />
			)
		},
	}),
]
const MarketLabel = ({ item }: { item: CompetitiveDataPointExtended }) => {
	const link = item.url ? (
		<a className='truncate' target='_blanc' href={item.url}>
			{item.label}
		</a>
	) : (
		<span>{item.label}</span>
	)

	return (
		<Tooltip>
			<TooltipTrigger asChild>{link}</TooltipTrigger>
			<TooltipContent>{item.label}</TooltipContent>
		</Tooltip>
	)
}
type PointProps = {
	color: string
	onChange(checked: boolean): void
	checked?: boolean
}
const Point = ({ color, onChange, checked = false, ...props }: PointProps) => (
	<div
		{...props}
		className={`color-dot mr-2 inline-block cursor-pointer rounded-lg ${!checked ? 'bg-accent' : ''}`}
		style={{ background: color }}
		onClick={() => onChange(!!checked)}
	/>
)
